import { useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import stile from './Popup.module.css';
import faehigkeitenDaten from '../data/mySkills.json';
import { useSprache } from '../sprache';

function Popup({ tech, setShowPopup, selectedCardRect }) {
  const popupReferenz = useRef(null);
  const huellReferenz = useRef(null);
  const listeReferenz = useRef(null);
  const { sprache, t } = useSprache();

  useLayoutEffect(() => {
    // Scrollen im Hintergrund sperren
    document.body.style.overflow = 'hidden';

    const popupElement = popupReferenz.current;
    const huellElement = huellReferenz.current;

    if (!popupElement || !huellElement) return;

    // Sanfte, hardware-beschleunigte Einblendung ohne Layout-Verzerrungen
    const zeitleiste = gsap.timeline();

    // 1. Hintergrund-Schleier einblenden
    zeitleiste.fromTo(
      huellElement,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: 'power2.out' }
    );

    // 2. Modalfenster flüssig skalieren und einblenden (rein über Transform/Opacity)
    zeitleiste.fromTo(
      popupElement,
      {
        opacity: 0,
        scale: 0.88,
        y: 25,
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.4,
        ease: 'power3.out',
      },
      '-=0.2'
    );

    // 3. Stufenweises Erscheinen der einzelnen Wissenseinträge
    if (listeReferenz.current) {
      const eintraege = listeReferenz.current.querySelectorAll(`.${stile.wissensEintrag}`);
      if (eintraege.length > 0) {
        gsap.fromTo(
          eintraege,
          { opacity: 0, y: 12 },
          {
            opacity: 1,
            y: 0,
            duration: 0.35,
            stagger: 0.05,
            ease: 'power2.out',
            delay: 0.1,
          }
        );
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const popupSchliessen = () => {
    const popupElement = popupReferenz.current;
    const huellElement = huellReferenz.current;

    if (!popupElement || !huellElement) {
      setShowPopup(false);
      document.body.style.overflow = '';
      return;
    }

    // Flüssige Schließanimation
    const zeitleiste = gsap.timeline({
      onComplete: () => {
        setShowPopup(false);
        document.body.style.overflow = '';
      },
    });

    zeitleiste.to(popupElement, {
      opacity: 0,
      scale: 0.92,
      y: 15,
      duration: 0.22,
      ease: 'power2.in',
    });

    zeitleiste.to(
      huellElement,
      {
        opacity: 0,
        duration: 0.22,
        ease: 'power2.in',
      },
      '-=0.15'
    );
  };

  // Details zur gewählten Technologie ermitteln
  const gewaehlteFaehigkeit = faehigkeitenDaten.find((kompetenz) => kompetenz.title === tech);
  const wissen = sprache === 'en' ? gewaehlteFaehigkeit?.knowledgeEn : gewaehlteFaehigkeit?.knowledge;

  // Per Portal direkt in <body> rendern: #root bildet mit z-index: 2 einen eigenen
  // Stacking-Context, darin kaeme das Popup nie ueber die Navbar (z-3, ausserhalb von #root).
  return createPortal(
    <div
      className={stile.popupWrapper}
      onClick={popupSchliessen}
      key={tech}
      ref={huellReferenz}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-titel"
    >
      <div
        className={stile.popup}
        onClick={(ereignis) => ereignis.stopPropagation()}
        ref={popupReferenz}
      >
        <div className={stile.popupKopfzeile}>
          <h3 className={stile.popupTitel} id="popup-titel">
            {tech}
          </h3>
          <button
            type="button"
            className={stile.closePopup}
            onClick={popupSchliessen}
            aria-label={t('popup.schliessen')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
            </svg>
          </button>
        </div>

        {wissen ? (
          <ul className={stile.wissensListe} ref={listeReferenz}>
            {wissen.map((wissenspunkt, index) => (
              <li key={index} className={stile.wissensEintrag}>
                <span className={stile.wissensPunkt}>✓</span>
                <span>{wissenspunkt}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={stile.keineDatenHinweis}>
            {t('popup.keineDaten')}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Popup;
